import { PropsWithChildren, useState } from 'react';
import { DialogContent, TextField, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { GroupView } from '@views';
import { ToDate } from '@utils/DateUtils';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { SurveyStatus } from '@app/models/Survey';

interface Props extends PropsWithChildren {
  group: GroupView;
  onGroupChange: (e: any) => void;
  surveys: SurveyStatus[];
  selectedSurveyIds: number[];
  onSurveyIdsChange: (ids: number[]) => void;
}

export const GroupDialogContent: React.FC<Props> = ({
  children,
  group,
  onGroupChange,
  surveys,
  selectedSurveyIds,
  onSurveyIdsChange,
}) => {
  const [roleNameError, setRoleNameError] = useState(false);
  const [roleNameHelperText, setRoleNameHelperText] = useState('');
  const validateRoleName = () => {
    if (group.name.length < 1) {
      setRoleNameError(true);
      setRoleNameHelperText('Role name is required');
    } else {
      setRoleNameError(false);
      setRoleNameHelperText('');
    }
  };

  return (
    <DialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        overflow: 'scroll',
      }}
    >
      <TextField
        label={'Group ID'}
        value={group.id}
        disabled={true}
        slotProps={{
          input: {
            readOnly: true,
            hidden: true,
          },
        }}
        fullWidth={true}
        rows={1}
      >
        {group.id}
      </TextField>

      <TextField
        required
        id={'name'}
        onChange={onGroupChange}
        value={group.name}
        label={'Group Name'}
        error={roleNameError}
        onKeyUp={validateRoleName}
        fullWidth={true}
        variant={'outlined'}
        helperText={roleNameHelperText}
      ></TextField>

      <DatePicker
        label={'Start Date'}
        key={'startDate'}
        value={ToDate(group.startDate)}
        onChange={onGroupChange}
      ></DatePicker>

      <TextField
        id={'shortDescription'}
        onChange={onGroupChange}
        value={group.shortDescription || ''}
        label={'Short Description'}
        multiline
        fullWidth={true}
        rows={4}
        variant={'outlined'}
        content={group.shortDescription}
      ></TextField>

      <TextField
        id={'userCount'}
        onChange={onGroupChange}
        value={group.userCount}
        label={'Expected Number of Participants'}
        type={'number'}
        fullWidth={true}
        rows={4}
        variant={'outlined'}
      ></TextField>

      <FormControl fullWidth variant="outlined">
        <InputLabel id="inPersonCelebration-label">
          Should cohort celebration survey include an in-person option?
        </InputLabel>
        <Select
          labelId="inPersonCelebration-label"
          id="inPersonCelebration"
          name="inPersonCelebration"
          label="Should cohort celebration survey include an in-person option?"
          value={group.inPersonCelebration ? 'yes' : 'no'}
          onChange={onGroupChange}
          disabled={group.currentWeekNumberIndexZero >= 11}
        >
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </Select>
      </FormControl>

      {surveys && surveys.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Survey Assignment
          </Typography>
          <Box>
            {surveys.map((survey) => (
              <FormControlLabel
                key={survey.id}
                control={
                  <Checkbox
                    checked={selectedSurveyIds?.includes(survey.id) || false}
                    onChange={() =>
                      onSurveyIdsChange(
                        selectedSurveyIds.includes(survey.id)
                          ? selectedSurveyIds.filter((id) => id !== survey.id)
                          : [...selectedSurveyIds, survey.id]
                      )
                    }
                  />
                }
                label={survey.title}
              />
            ))}
          </Box>
        </>
      )}

      {children}
    </DialogContent>
  );
};
